/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import UserModal from "../components/modals/UserModal";
import LockOverrideModal from "../components/modals/LockOverrideModal";
import {
  CheckedIcon,
  DropDown,
  Edit,
  Filter,
  LockIcon,
  Search,
  ShowMoreIcon,
} from "../components/icons";
import {
  useCreateUserMutation,
  useGetCampusesQuery,
  useGetRolesQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
  useUpdateUserAccountStatusMutation,
} from "../store/api";
import { Ban, PauseCircle } from "lucide-react";
import toast from "react-hot-toast";

function AccountStatusBadge({ status }) {
  const normalized = (status || "").toLowerCase();

  if (normalized === "banned") {
    return (
      <p className="text-[#CA0707] capitalize flex items-center gap-2.5">
        <Ban size={20} strokeWidth={2} />
        <span>{status}</span>
      </p>
    );
  }

  if (normalized === "suspended") {
    return (
      <p className="text-[#986012] capitalize flex items-center gap-2.5">
        <PauseCircle size={20} strokeWidth={2} />
        <span>{status}</span>
      </p>
    );
  }

  return (
    <p className="text-[#10A142] capitalize flex items-center gap-2.5">
      <CheckedIcon />
      <span>{status}</span>
    </p>
  );
}

export default function Users() {
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [role, setRole] = useState(null);
  const [roleOpen, setRoleOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const campRef = useRef(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState(null);
  const [lockOverrideOpen, setLockOverrideOpen] = useState(false);
  const [lockOverrideUser, setLockOverrideUser] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);

  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [updateUserAccountStatus] = useUpdateUserAccountStatusMutation();

  const { data: campusesData, isLoading: campusesLoading } =
    useGetCampusesQuery(1);
  const { data: rolesData } = useGetRolesQuery({
    page: 1,
  });

  const campusOptions = campusesData?.data?.campuses || [];
  const campusDropdownOptions = [
    { _id: "", name: "All Campuses" },
    ...campusOptions,
  ];

  const roleOptions = [
    { _id: "", roleName: "All Roles" },
    ...(rolesData?.data || []),
  ];



  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCampus, role]);

  const { data: usersData, isFetching } = useGetUsersQuery({
    page,
    search: debouncedSearch,
    campus: selectedCampus?._id || "",
    role: role?._id || "",
  });
  const rows = usersData?.data?.users || [];
  const totalPages = usersData?.data?.totalPages || 1;

  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));

  useEffect(() => {
    function handleClickOutside(event) {
      if (!event.target.closest("[data-user-menu]")) {
        setMenuUserId(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = [
    { key: "identity", label: "Identity", className: "col-span-3" },
    {
      key: "campus",
      label: "Assigned Campus",
      className: "col-span-2 text-center ",
    },
    {
      key: "role",
      label: "Roles",
      className: "col-span-3 text-center",
    },
    {
      key: "status",
      label: "Account Status",
      className: "col-span-2 text-center",
    },
    { key: "actions", label: "Actions", className: "col-span-2 text-center" },
  ];

  const openCreateModal = () => {
    setModalMode("add");
    setSelectedUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setModalMode("edit");
    setSelectedUser(user);
    setModalOpen(true);
  };

  const openLockOverrideModal = (user) => {
    setLockOverrideUser(user);
    setLockOverrideOpen(true);
  };

  const handleUserSubmit = async (payload, mode = "add", userId) => {
    try {
      if (mode === "edit" && userId) {
        await updateUser({ id: userId, ...payload }).unwrap();
        toast.success("User updated successfully!");
      } else {
        await createUser(payload).unwrap();
        toast.success("User created successfully!");
      }
      setModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Failed to save user", error);
      toast.error(error?.data?.message || "Failed to save user");
    }
  };

  const handleAccountStatusChange = async (userId, status) => {
    try {
      await updateUserAccountStatus({ id: userId, status }).unwrap();
      setMenuUserId(null);
      toast.success(`User status updated to ${status}`);
    } catch (error) {
      console.error("Failed to update account status", error);
      toast.error(error?.data?.message || "Failed to update account status");
    }
  };

  const renderRow = (r, idx) => (
    <div
      key={r._id}
      className="bg-[#EEEEEE40]"
    >
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5]">
        <div className="col-span-3 flex items-center gap-2 text-black">
          <div className="w-[37px] h-[37px] flex items-center justify-center overflow-hidden rounded-full bg-[#6D4E8A] text-white">
            {r?.profilePicture?.secure_url ? (
              <img src={r?.profilePicture?.secure_url} alt="profile " className="" />
            ) : (
              <span>{r?.name?.charAt(0)}</span>
            )}
          </div>
          <div>
            <div className="leading-5">{r.name}</div>
            <div className="text-xs leading-4 text-[#898989]">{r.email}</div>
          </div>
        </div>

        <div className="col-span-2 text-center">
          <p className="text-black text-xs leading-6">{r.campus.name}</p>
        </div>

        <div className="col-span-3 flex justify-center">
          <div
            className={` flex flex-wrap gap-2 `}
          >
            {r.roles.map((role) => (
              <p className="border border-[#CECECE] bg-[#EFEFEF] rounded-[10px]  text-center text-black py-[5px] px-4" key={role._id}>{role.roleName}</p>
            ))}
          </div>
        </div>

        <div className="col-span-2 flex justify-center">
          <AccountStatusBadge status={r.accountStatus} />
        </div>

        <div className="col-span-2 flex justify-center gap-4">
          <button aria-label="edit" onClick={() => openEditModal(r)}>
            <Edit />
          </button>
          <button
            aria-label="lock override"
            onClick={() => openLockOverrideModal(r)}
          >
            <LockIcon />
          </button>
          <div className="relative" data-user-menu>
            <button
              aria-label="more"
              onClick={(e) => {
                e.stopPropagation();
                setMenuUserId((prev) => (prev === r._id ? null : r._id));
              }}
            >
              <ShowMoreIcon />
            </button>

            {menuUserId === r._id && (
              <div className="absolute right-0 top-8 z-20 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                <button
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccountStatusChange(r._id, "active");
                  }}
                >
                  Set Active
                </button>
                <button
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccountStatusChange(r._id, "suspended");
                  }}
                >
                  Set Suspended
                </button>
                <button
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccountStatusChange(r._id, "banned");
                  }}
                >
                  Set Banned
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-6 md:pt-14 px-4 md:pl-8 md:pr-12 min-h-screen">
      {/* #sym:HeaderInfo */}
      <HeaderInfo
        title="User Management"
        desc="Control system access and assign specialized roles"
        btnLabel="Create New User"
        onAdd={openCreateModal}
      />
      <div className="mt-4 md:mt-7 ml-0 md:ml-2.5">
        <div className="border border-[#D9D9D9] rounded-[10px] py-4 px-4 md:pl-6 md:pr-4 flex flex-col xl:flex-row items-stretch xl:items-center gap-4 xl:gap-2">
          <div className="flex items-center max-w-full xl:max-w-[496px] w-full gap-2 flex-1 py-3 px-4 bg-[#EEEEEE40] rounded-[10px]">
            <Search />
            <input
              type="text"
              name="users-list-filter"
              autoComplete="off"
              placeholder="Search by Name or Email"
              className="w-full bg-transparent  outline-none text-black  placeholder:text-[#878686]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full">
              <div className="relative w-full sm:w-auto" ref={menuRef}>
                <button
                  onClick={() => setRoleOpen((s) => !s)}
                  className="w-full sm:w-[183px] flex items-center justify-between gap-3 text-black bg-[#EEEEEE40] px-6 py-3 rounded-[10px]"
                  aria-haspopup="menu"
                  aria-expanded={roleOpen}
                >
                  <span>{role?._id ? role.roleName : "All Roles"}</span>
                  <DropDown color="#000" />
                </button>

                {roleOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-30">
                    {roleOptions.map((opt) => (
                      <button
                        key={opt._id}
                        onClick={() => {
                          setRole(opt);
                          setRoleOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${opt === role ? "font-medium" : "font-normal"}`}
                      >
                        {opt.roleName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative w-full sm:w-auto" ref={campRef}>
                <button
                  className="w-full sm:w-[183px] flex items-center justify-between gap-3 text-black bg-[#EEEEEE40] px-6 py-3 rounded-[10px]"
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={() => setOpen((s) => !s)}
                >
                  <span>
                    {selectedCampus?._id ? selectedCampus.name : "All Campuses"}
                  </span>
                  <DropDown color="#000" />
                </button>
                {open && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-30">
                    {campusDropdownOptions.map((opt) => (
                      <button
                        key={opt?._id}
                        onClick={() => {
                          setSelectedCampus(opt);
                          setOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${(opt?._id || "") === (selectedCampus?._id || "") ? "font-medium" : "font-normal"}`}
                      >
                        {opt?.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 ml-0 md:ml-2.5">
          <DataTable
            columns={columns}
            rows={rows}
            renderRow={renderRow}
            page={page}
            totalPages={totalPages}
            onNext={handleNext}
            onPrev={handlePrev}
            isLoading={isFetching}
          />
        </div>

        <UserModal
          open={modalOpen}
          mode={modalMode}
          initialValues={selectedUser}
          onClose={() => {
            setModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={(payload, mode, userId) => handleUserSubmit(payload, mode, userId)}
          isLoading={modalMode === "edit" ? isUpdatingUser : isCreatingUser}
          campusOptions={campusOptions}
          roleOptions={rolesData?.data || []}
        />

        <LockOverrideModal
          open={lockOverrideOpen}
          userId={lockOverrideUser?._id}
          userName={lockOverrideUser?.name}
          onClose={() => {
            setLockOverrideOpen(false);
            setLockOverrideUser(null);
          }}
        />
      </div>
    </div>
  );
}
